import { Component, h, Element, Prop } from "@stencil/core"
import { ExpandAndCollapseArrowPosition } from "./types"
import { getAppearance } from "../../core/helpers/webcomponent-helpers"

@Component({
  tag: "sdx-expand-and-collapse",
  styleUrl: "expand-and-collapse.scss",
  shadow: true,
})
export class ExpandAndCollapse {
  @Element() public el!: HTMLSdxButtonElement
  /**
   * Emitted when the component's visibility updates.
   * @event
   * @param display "open" | "opening" | "closed" | "closing"
   */
  // @ts-ignore just for documentation purposes
  private static eventSdxDisplayChange = "sdxdisplaychange"

  /**
   * If the component is open or closed.
   */
  @Prop({ mutable: true }) public open: boolean = false

  /**
   * Label that shows when the component is collapsed and ready to be expanded.
   */
  @Prop() public expandLabel: string = ""

  /**
   * Label that shows when the component is expanded and ready to be collapsed.
   */
  @Prop() public collapseLabel: string = ""

  /**
   * Position of the collapse label (above or below the expanded content)
   */
  @Prop() public arrowPosition: ExpandAndCollapseArrowPosition = "fixed"

  /**
   * Description text read by the screen reader. Will be appended to the button content.
   */
  @Prop() public srHint: string = ""

  private toggle() {
    this.open = !this.open
  }

  private getAccordionStyles(): Record<
    string,
    Record<string, string | undefined>
  > {
    return {
      accordion: {
        border: "none",
      },
      header: {
        fontSize: "inherit",
        padding: "0",
        justifyContent: "flex-end",
        alignItems: "flex-start",
      },
      body: {
        margin: this.arrowPosition === "dynamic" ? "0 0 8px 0" : "8px 0 0 0",
      },
      arrow: {
        top: "12px", // align arrow with first line of label's text
      },
    }
  }

  private getComponentClassNames() {
    return {
      component: true,
      [getAppearance(this.el)]: true,
    }
  }

  private createHeader() {
    const { arrow, header } = this.getAccordionStyles()

    return (
      <sdx-accordion-item-header
        id="header"
        onClick={() => this.toggle()}
        arrowStyle={arrow}
        buttonStyle={header}
      >
        <span>{this.open ? this.collapseLabel : this.expandLabel}</span>
        {this.srHint && <span class="sr-only">&nbsp;{this.srHint}</span>}
      </sdx-accordion-item-header>
    )
  }

  public render() {
    return (
      <sdx-accordion
        arrowPosition="left"
        componentStyle={this.getAccordionStyles().accordion}
        class={this.getComponentClassNames()}
      >
        <sdx-accordion-item open={this.open}>
          {this.arrowPosition === "fixed" && this.createHeader()}

          <sdx-accordion-item-body
            role="region"
            aria-labelledby="header"
            componentStyle={this.getAccordionStyles().body}
          >
            <slot />
          </sdx-accordion-item-body>

          {this.arrowPosition === "dynamic" && this.createHeader()}
        </sdx-accordion-item>
      </sdx-accordion>
    )
  }
}
